import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/shared/lib/api';
import { useAuthStore } from '@store/auth.store';
import { useUIStore } from '@store/ui.store';
import type { AuthTokens, User } from '@/shared/types';
import type { TwoFactorSetupData } from '@/shared/components/TwoFactorEnroll';

// B-162: logowanie kończy się na jeden z trzech sposobów — sesja, wyzwanie
// kodem (2FA skonfigurowane) albo wymuszona konfiguracja (administrator
// oznaczył konto, a 2FA jeszcze nie ma).
type LoginResult =
  | AuthTokens
  | { twoFactorRequired: true; twoFactorToken: string }
  | { twoFactorSetupRequired: true; twoFactorToken: string };

/** Shared finisher: persist tokens + route, for both the password-only and 2FA paths. */
export function useFinishLogin() {
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  return (data: AuthTokens) => {
    setAuth(data.user as User, data.accessToken, data.refreshToken);
    toast.success(`Witaj ponownie, ${data.user.firstName}!`);
    navigate(data.user.mustChangePassword ? '/change-password' : '/');
  };
}

export function useLogin() {
  const finishLogin = useFinishLogin();

  return useMutation<LoginResult, any, { email: string; password: string }>({
    // app:'admin' → backend login gate rejects roles without admin-panel access
    // (403, message surfaced by onError below). The instructor PWA sends 'instructor'.
    mutationFn: (credentials) => api.post<LoginResult>('/auth/login', { ...credentials, app: 'admin' }),
    onSuccess: (data) => {
      // 2FA path is handled by the caller (LoginPage swaps to the verify step).
      if ('twoFactorRequired' in data && data.twoFactorRequired) return;
      if ('twoFactorSetupRequired' in data && data.twoFactorSetupRequired) return;
      finishLogin(data as AuthTokens);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Logowanie nie powiodło się. Spróbuj ponownie.';
      toast.error(Array.isArray(message) ? message[0] : message);
    },
  });
}

/** Step 2 of login when 2FA is enabled. */
export function useVerify2fa() {
  const finishLogin = useFinishLogin();
  return useMutation<AuthTokens, any, { twoFactorToken: string; code: string }>({
    mutationFn: (payload) => api.post<AuthTokens>('/auth/2fa/verify', payload),
    onSuccess: (data) => finishLogin(data),
  });
}

/** B-162 krok 2: sekret + QR na podstawie tokenu wyzwania (bez sesji). */
export function useSetup2faChallenge() {
  return useMutation<TwoFactorSetupData, any, { twoFactorToken: string }>({
    mutationFn: (payload) => api.post<TwoFactorSetupData>('/auth/2fa/setup-challenge', payload),
  });
}

/**
 * B-162 krok 3: potwierdzenie kodem → 2FA włączone i logowanie dokończone.
 *
 * Celowo NIE woła finishLogin — kody zapasowe pokazujemy raz i tylko tutaj,
 * więc ekran musi je wyświetlić, zanim przejdziemy dalej. Sesję domyka
 * useFinishLogin dopiero po potwierdzeniu przez użytkownika.
 */
export function useEnable2faChallenge() {
  return useMutation<AuthTokens & { backupCodes: string[] }, any, { twoFactorToken: string; code: string }>({
    mutationFn: (payload) => api.post<AuthTokens & { backupCodes: string[] }>('/auth/2fa/enable-challenge', payload),
  });
}

/** Password-reset request (always succeeds — no enumeration). */
export function useForgotPassword() {
  return useMutation<{ ok: boolean }, any, { email: string }>({
    mutationFn: (payload) => api.post<{ ok: boolean }>('/auth/forgot-password', payload),
  });
}

export function useLogout() {
  const { logout, refreshToken } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => api.post('/auth/logout', { refreshToken }),
    onSettled: () => {
      // logout() also drops the query cache — for every logout path (B-523, shared/lib/queryClient.ts).
      logout();
      navigate('/login');
    },
  });
}

export function useProfile() {
  const { isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: () => api.get<User>('/auth/profile'),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * B-523: the account an access token was issued to (`sub`, set by AuthService.generateTokens) — the
 * account the API answers as, whatever the stored user says. Null when the token can't be read.
 */
function tokenSubject(token: string | null): string | null {
  try {
    const payload = token?.split('.')[1];
    if (!payload) return null;
    const sub = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).sub;
    return typeof sub === 'string' ? sub : null;
  } catch {
    return null;
  }
}

/**
 * Boot-time profile sync (self-heal for stale sessions).
 *
 * Mounted once in AppLayout: fetches /auth/profile when authenticated and
 * merges the fresh server-side fields — most importantly `permissions` and
 * `role` — into the persisted auth store. Sessions created before the RBAC
 * release have no `permissions` array in localStorage; this refreshes them
 * without forcing a re-login, and also picks up matrix changes made by an
 * admin while the user stays logged in.
 */
export function useProfileSync() {
  const { updateUser } = useAuthStore();
  const profileQ = useProfile();

  useEffect(() => {
    const profile = profileQ.data;
    // B-523: merge only the profile of the account this session's token belongs to. The query key is
    // the same for everyone and the merge is wholesale — a profile cached for the previous person on
    // this tab used to overwrite the stored id, name, role and permissions on top of the new person's
    // tokens. The cache is dropped on every account change (shared/lib/queryClient.ts); this guards
    // whatever slips past it. Anchored on the token, not on the stored user: a session stored by that
    // bug (user A, tokens B) still heals from B's own fresh profile.
    const { user, accessToken } = useAuthStore.getState();
    if (!profile || profile.id !== (tokenSubject(accessToken) ?? user?.id)) return;
    // Profile is the canonical user record — merge it wholesale (incl. permissions).
    updateUser(profile);
    // B-16: zastosuj preferencje UI z konta (motyw/akcent/kontrast/gęstość).
    useUIStore.getState().hydratePreferences((profile as any).uiPreferences);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileQ.dataUpdatedAt]);

  return profileQ;
}

export function useChangePassword() {
  const { updateUser } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.post('/auth/change-password', data),
    onSuccess: () => {
      // Wyczyść flagę wymuszonej zmiany hasła w storze
      updateUser({ mustChangePassword: false });
      toast.success('Hasło zostało pomyślnie zmienione');
      navigate('/');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Nie udało się zmienić hasła');
    },
  });
}
